import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  getDirectEmissionsDataAPI,
  postWasteAndOtherActivitiesOtherFuelPreviewAPI,
} from 'src/utils/api';
import { mapMobileFuelToTypeOfFuel } from 'src/Ghg/utils/fuelTypeMappings';
import { toLocaleNumber } from 'src/utils/helpers';

type Scope3OtherFuelRow = {
  type_of_fuel: string;
  amount_per_period: string;
  unit: string;
  manual_factor: null;
  comment: string | null;
};

type PreviewRow = {
  id: string;
  source: 'stationary' | 'mobile';
  include: boolean;
  type_of_fuel: string;
  amount_per_period: number;
  unit: string;
  manual_factor: null;
  comment: string | null;
  emission_kg_eq_co2: number;
};

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const s = String(value).trim().replace(',', '.');
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export default function ImportScope1FuelToScope3Modal({
  open,
  onClose,
  orgId,
  period,
  language,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  orgId: number;
  period: string;
  language: string;
  onConfirm: (_items: Scope3OtherFuelRow[]) => void;
}) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [skippedCount, setSkippedCount] = useState(0);
  const [rows, setRows] = useState<PreviewRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!open) return;
      setLoading(true);
      setError(null);
      setRows([]);
      setSkippedCount(0);

      try {
        const scope1 = await getDirectEmissionsDataAPI(orgId, period);

        const candidates: Omit<PreviewRow, 'emission_kg_eq_co2'>[] = [];
        let skipped = 0;
        let candidateSeq = 0;

        const stationary = (scope1?.stationary_combustion ?? []) as any[];
        for (const entry of stationary) {
          const typeOfFuel = entry?.type_of_fuel;
          const unit = entry?.unit;
          const amount = toNumber(entry?.amount_per_period);

          if (!typeOfFuel || !unit || amount === null) continue;
          if (typeOfFuel === 'other.manual_factor') {
            skipped += 1;
            continue;
          }

          const equipmentName = entry?.name_of_equipement ?? '';
          candidates.push({
            id: `stationary:${String(typeOfFuel)}:${String(unit)}:${String(
              equipmentName
            )}:${String(amount)}:${candidateSeq++}`,
            source: 'stationary',
            include: true,
            type_of_fuel: typeOfFuel,
            unit,
            amount_per_period: amount,
            manual_factor: null,
            comment: equipmentName ? String(equipmentName) : null,
          });
        }

        const mobile = (scope1?.mobile_combustion_consumptionwise ??
          []) as any[];
        for (const entry of mobile) {
          const mobileType = entry?.type_of_fuel;
          const mapped = mobileType
            ? mapMobileFuelToTypeOfFuel(mobileType)
            : null;
          const unit = entry?.unit_of_fuel;
          const amount = toNumber(entry?.amount_per_period);

          if (!mobileType || !unit || amount === null) continue;
          if (!mapped) {
            skipped += 1;
            continue;
          }

          const equipmentName = entry?.name_of_equipement ?? '';
          candidates.push({
            id: `mobile:${String(mapped)}:${String(unit)}:${String(
              equipmentName
            )}:${String(amount)}:${candidateSeq++}`,
            source: 'mobile',
            include: true,
            type_of_fuel: mapped,
            unit,
            amount_per_period: amount,
            manual_factor: null,
            comment: equipmentName ? String(equipmentName) : null,
          });
        }

        if (cancelled) return;
        setSkippedCount(skipped);

        if (candidates.length === 0) {
          setRows([]);
          setLoading(false);
          return;
        }

        const preview = await postWasteAndOtherActivitiesOtherFuelPreviewAPI(
          orgId,
          period,
          {
            items: candidates.map((c) => ({
              type_of_fuel: c.type_of_fuel,
              unit: c.unit,
              amount_per_period: c.amount_per_period,
              manual_factor: null,
              comment: c.comment,
            })),
          }
        );

        if (cancelled) return;
        const withEmissions: PreviewRow[] = candidates.map((c, idx) => ({
          ...c,
          emission_kg_eq_co2: preview[idx]?.emission_kg_eq_co2 ?? 0,
        }));

        setRows(withEmissions);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message ?? String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [open, orgId, period]);

  const selected = useMemo(() => rows.filter((r) => r.include), [rows]);
  const totalEmission = useMemo(
    () => selected.reduce((acc, r) => acc + (r.emission_kg_eq_co2 ?? 0), 0),
    [selected]
  );

  const setAll = (value: boolean) => {
    setRows((prev) => prev.map((r) => ({ ...r, include: value })));
  };

  const toggleRow = (id: string) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, include: !r.include } : r))
    );
  };

  const handleConfirm = () => {
    const items: Scope3OtherFuelRow[] = selected.map((r) => ({
      type_of_fuel: r.type_of_fuel,
      unit: r.unit,
      amount_per_period: String(r.amount_per_period),
      manual_factor: null,
      comment: r.comment,
    }));
    onConfirm(items);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('waste-and-other.other-fuel.importFromScope1.title')}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 1 }}>
          {t('waste-and-other.other-fuel.importFromScope1.description')}
        </Typography>

        {skippedCount > 0 && (
          <Typography sx={{ mb: 2 }} color="text.secondary">
            {t('waste-and-other.other-fuel.importFromScope1.skipped', {
              count: skippedCount,
            })}
          </Typography>
        )}

        {loading && (
          <Typography>
            {t('waste-and-other.other-fuel.importFromScope1.loading')}
          </Typography>
        )}
        {!!error && (
          <Typography color="error" sx={{ whiteSpace: 'pre-wrap' }}>
            {t('waste-and-other.other-fuel.importFromScope1.error')}: {error}
          </Typography>
        )}

        {!loading && !error && rows.length === 0 && (
          <Typography color="text.secondary">
            {t('waste-and-other.other-fuel.importFromScope1.empty')}
          </Typography>
        )}

        {!loading && !error && rows.length > 0 && (
          <>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selected.length === rows.length}
                    indeterminate={
                      selected.length > 0 && selected.length < rows.length
                    }
                    onChange={(e) => setAll(e.target.checked)}
                  />
                }
                label={t(
                  'waste-and-other.other-fuel.importFromScope1.selectAll'
                )}
              />
              <Typography sx={{ ml: 'auto' }}>
                {t('waste-and-other.other-fuel.importFromScope1.total', {
                  value: toLocaleNumber(totalEmission, language),
                })}
              </Typography>
            </div>

            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>
                    {t(
                      'waste-and-other.other-fuel.importFromScope1.table.source'
                    )}
                  </TableCell>
                  <TableCell>
                    {t('waste-and-other.other-fuel.type_of_fuel')}
                  </TableCell>
                  <TableCell>
                    {t('waste-and-other.other-fuel.amount_per_period')}
                  </TableCell>
                  <TableCell>{t('waste-and-other.other-fuel.unit')}</TableCell>
                  <TableCell align="right">
                    {t(
                      'waste-and-other.other-fuel.importFromScope1.table.emissions'
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={r.include}
                        onChange={() => toggleRow(r.id)}
                      />
                    </TableCell>
                    <TableCell>
                      {r.source === 'stationary'
                        ? t(
                            'waste-and-other.other-fuel.importFromScope1.source.stationary'
                          )
                        : t(
                            'waste-and-other.other-fuel.importFromScope1.source.mobile'
                          )}
                    </TableCell>
                    <TableCell>{t(`dropdowns.${r.type_of_fuel}`)}</TableCell>
                    <TableCell>
                      {toLocaleNumber(r.amount_per_period, language)}
                    </TableCell>
                    <TableCell>{t(`dropdowns.${r.unit}`)}</TableCell>
                    <TableCell align="right">
                      {toLocaleNumber(r.emission_kg_eq_co2, language)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || !!error || selected.length === 0}
        >
          {t('waste-and-other.other-fuel.importFromScope1.addSelected')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
